import { useMemo, type FunctionComponent } from "react";
import { EyeInvisibleOutlined, EyeOutlined, InfoCircleOutlined, LinkOutlined } from "@ant-design/icons";
import { message, Space, Switch, Table, Tag, Tooltip, Typography } from "antd";
import { type TableProps } from "antd/lib/table/index";
import keyBy from "lodash.keyby";
import orderBy from "lodash.orderby";
import truncate from "lodash.truncate";
import { useTranslation } from "react-i18next";
import { v4 as uuid } from "uuid";

import { productActions } from "@fitness-app/app-store";
import {
  CheckoutVariantStatus,
  type CheckoutVariant,
  type CheckoutVariantPriceStats,
} from "@fitness-app/data-models/entities/CheckoutVariant";
import {
  PriceStatus,
  ProductPriceAction,
  type ProductPrice,
  type StripePrice,
} from "@fitness-app/data-models/entities/Product";
import { getProductLink } from "@fitness-app/utils/src/products/getProductLink";

import DraggableTable from "~/components/DraggableTable/DraggableTable";
import { useCheckoutVariants } from "~/modules/Products/Product/ProductDetails/hooks/useCheckoutVariants";
import EditPriceLabel from "~/modules/Products/Product/ProductPrices/EditPriceLabel";
import PriceConfig from "~/modules/Products/Product/ProductPrices/PriceConfig";
import { useAppDispatch, useAppSelector } from "~/store/initializeStore";
import { getPriceFormatter } from "../../utils/getPriceFormatter";
import EditCustomInvoiceName from "./EditCustomInvoiceName";

interface OwnProps {
  prices: ProductPrice[];
  productSlug: string;
  productId: string;
  productName?: string;
  extended?: boolean;
  readonly?: boolean;
  allowCopyLink?: boolean;
  onPriceOrderUpdate?: (prices: ProductPrice[]) => void;
  showPriceLabel?: boolean;
  allowDnd?: boolean;
  pricesStats?: Record<string, CheckoutVariantPriceStats>;
  /** Passed straight to the table; `false` (the default) keeps every price on one page. */
  pagination?: TableProps<ProductPrice>["pagination"];
}

type Props = OwnProps;

type Price = ProductPrice;
const ProductPrices: FunctionComponent<Props> = ({
  prices,
  productSlug,
  productId,
  extended = false,
  readonly,
  allowCopyLink = true,
  onPriceOrderUpdate,
  showPriceLabel = false,
  allowDnd = false,
  pricesStats,
  pagination = false,
}) => {
  const { t } = useTranslation("products");
  const dispatch = useAppDispatch();
  const productPricesActions = useAppSelector((store) => store.product.productPricesActions);
  const { data: checkoutVariants } = useCheckoutVariants(productId);
  const pricesWithoutArchived = orderBy(prices, "sort", "asc").filter((price) => price.status !== PriceStatus.Archived);

  // Indexed once instead of scanning every variant per row — a product with hundreds of
  // prices otherwise pays for that scan on each render of each row.
  const variantsByPriceId = useMemo(() => {
    const index = new Map<string, CheckoutVariant[]>();

    for (const variant of checkoutVariants ?? []) {
      for (const priceId of variant.prices) {
        const variants = index.get(priceId);
        if (variants) {
          variants.push(variant);
        } else {
          index.set(priceId, [variant]);
        }
      }
    }

    return index;
  }, [checkoutVariants]);

  const getVariantsForPrice = (priceId: string) => variantsByPriceId.get(priceId) ?? [];

  const handlePricesOrderUpdate = (draggabledId: string, destinationIndex: number, sourceIndex: number) => {
    const pricesIds = prices.map((p) => p.id);
    if (!pricesIds.includes(draggabledId)) {
      return;
    }

    pricesIds.splice(sourceIndex, 1);
    pricesIds.splice(destinationIndex, 0, draggabledId);
    const pricesById = keyBy(prices, "id");

    const updatedPrices = pricesIds.map((id) => pricesById[id]).filter((price): price is StripePrice => !!price);

    if (onPriceOrderUpdate) {
      onPriceOrderUpdate(updatedPrices);
    } else {
      void dispatch(
        productActions.updateProductPricesOrder({
          productId,
          prices: updatedPrices,
        }),
      );
    }
  };

  const pricesSorted = useMemo(
    () => (allowDnd ? pricesWithoutArchived : orderBy(pricesWithoutArchived, "createdAt", "desc")),
    [pricesWithoutArchived, allowDnd],
  );

  return (
    <div className="my-6">
      <DraggableTable<ProductPrice>
        onUpdateListOrder={handlePricesOrderUpdate}
        rowKey="id"
        dndDisabled={!allowDnd}
        dataSource={pricesSorted}
        pagination={pagination}
        scroll={{ x: true }}
      >
        <Table.Column<Price> hidden={!allowDnd} width={50} dataIndex="sort" key="sort" />
        <Table.Column<Price>
          title={t("list.columns.index")}
          width={50}
          dataIndex="index"
          key="index"
          render={(_, __, index) => index + 1}
        />
        <Table.Column<Price>
          title="ID"
          dataIndex="id"
          key="id"
          render={(id: string) => (
            <Typography.Paragraph
              className="whitespace-nowrap"
              copyable={{ tooltips: [id, t("common:copied")], text: id }}
              style={{ marginBottom: 0 }}
            >
              {truncate(id, { length: 10 })}
            </Typography.Paragraph>
          )}
        />
        <Table.Column<Price>
          title={t("list.columns.price")}
          dataIndex="price"
          key="price"
          render={(_, price) => {
            if (!price.unit_amount) {
              return <span>-</span>;
            }

            if (extended && price.config?.showAsPromo) {
              return (
                <Space direction="horizontal">
                  <Typography.Paragraph className="whitespace-nowrap" type="danger" style={{ marginBottom: 0 }}>
                    {price.currency === "pln"
                      ? String(price.unit_amount / 100).replace(".", ",")
                      : price.unit_amount / 100}{" "}
                    {price.currency.toUpperCase()}
                  </Typography.Paragraph>
                  <Typography.Paragraph className="whitespace-nowrap" delete style={{ marginBottom: 0 }}>
                    {price.currency === "pln"
                      ? String(price.config.showAsPromo.priceBeforeDiscount / 100).replace(".", ",")
                      : price.config.showAsPromo.priceBeforeDiscount / 100}{" "}
                    {price.currency.toUpperCase()}
                  </Typography.Paragraph>
                </Space>
              );
            }

            return (
              <span>
                {price.currency === "pln" ? String(price.unit_amount / 100).replace(".", ",") : price.unit_amount / 100}{" "}
                {price.currency.toUpperCase()}
              </span>
            );
          }}
        />
        <Table.Column<Price>
          title={t("list.columns.paymentType")}
          dataIndex="priceType"
          key="priceType"
          render={(_, price: Price) => {
            const { type, accessPeriod, id, customLabel, customInvoiceName } = price;
            const variantsForPrice = getVariantsForPrice(id);

            return (
              <div>
                <span className="whitespace-nowrap">
                  {t(`paymentTypeOptions.${type}`)}{" "}
                  {type === "recurring" || (type === "one_time" && accessPeriod)
                    ? `(${t(`${type}.${accessPeriod?.interval}`, {
                        count: accessPeriod?.interval_count,
                      })})`
                    : ""}
                  {price.accessPeriod?.type === "one_time" && price.accessPeriod.endAccessStrategy ? (
                    <Tooltip
                      title={
                        <span>
                          {t("products:form.endAccessStrategy")}
                          {": "}
                          {t(`common:endAccessStrategy.${price.accessPeriod.endAccessStrategy}`)}
                        </span>
                      }
                    >
                      <InfoCircleOutlined style={{ marginLeft: 10, color: "#ffa800" }} />
                    </Tooltip>
                  ) : null}
                </span>
                {price.installments && (
                  <div className="whitespace-nowrap">
                    {t("paymentTypeOptions.installments")}
                    {price.installments}
                  </div>
                )}

                {extended || showPriceLabel ? (
                  <>
                    <div className="flex flex-col whitespace-nowrap">
                      <EditPriceLabel
                        id={id}
                        label={customLabel}
                        productId={productId}
                        type={type}
                        accessPeriod={accessPeriod}
                        readonly={!extended}
                      />
                    </div>
                    <div className="mt-5 flex">
                      <EditCustomInvoiceName
                        id={id}
                        readonly={!extended}
                        label={customInvoiceName}
                        productId={productId}
                      />
                    </div>
                  </>
                ) : null}

                {variantsForPrice.length > 0 && (
                  <div className="mt-2">
                    <div className="mb-1 text-xs text-gray-500">Checkout variants:</div>
                    <Space size="small" wrap>
                      {variantsForPrice.map((variant) => (
                        <Tag
                          key={variant.id}
                          color={variant.status === CheckoutVariantStatus.Active ? "green" : "default"}
                        >
                          {variant.slug}
                        </Tag>
                      ))}
                    </Space>
                  </div>
                )}
                {(price.customDuration ||
                  (price.config &&
                    (price.config.onlyForRenewal ||
                      price.config.forAccessProlongationNotRequirePayment ||
                      price.config.allowManualPayment ||
                      price.config.allowOverlapRenewalWithCurrentSubscription))) && (
                  <div className="mt-2">
                    <div className="mb-1 text-xs text-gray-500">Price config:</div>
                    <Space size="small" wrap>
                      {price.customDuration && (
                        <Tag color="geekblue">
                          {t("prices.customDuration")}:{" "}
                          {t(`recurring.${price.customDuration.unit}`, { count: price.customDuration.count })}
                        </Tag>
                      )}
                      {price.config?.onlyForRenewal && <Tag color="blue">{t("prices.onlyForRenewal")}</Tag>}
                      {price.config?.forAccessProlongationNotRequirePayment && (
                        <Tag color="orange">{t("prices.forAccessProlongationNotRequirePayment")}</Tag>
                      )}
                      {price.config?.allowManualPayment && (
                        <Tag color="purple">{t("prices.allowManualPaymentShort")}</Tag>
                      )}
                      {price.config?.allowOverlapRenewalWithCurrentSubscription && (
                        <Tag color="cyan">{t("prices.allowOverlapRenewalWithCurrentSubscription")}</Tag>
                      )}
                    </Space>
                  </div>
                )}
              </div>
            );
          }}
        />
        <Table.Column<Price>
          title={t("list.columns.totalClients")}
          dataIndex="clients"
          key="clients"
          render={(clients, price) => {
            if (!pricesStats) {
              return <span>{clients ?? 0}</span>;
            }

            const stats = pricesStats[price.id];
            const free = stats?.freeTransactions ?? 0;

            return (
              <span className="whitespace-nowrap">
                {stats?.transactions ?? 0}
                {free > 0 ? (
                  <Tooltip title={t("list.columns.freeTransactionsInfo")}>
                    <span className="ml-1 text-xs text-amber-600">({free} × 0 zł)</span>
                  </Tooltip>
                ) : null}
              </span>
            );
          }}
        />
        {pricesStats ? (
          <Table.Column<Price>
            title={t("list.columns.discountAmount", { defaultValue: "Wartość rabatów" })}
            dataIndex="discountAmount"
            key="discountAmount"
            render={(_, price) => (
              <span>
                {getPriceFormatter(price.currency).format((pricesStats[price.id]?.discountAmount ?? 0) / 100)}
              </span>
            )}
          />
        ) : null}
        {pricesStats ? (
          <Table.Column<Price>
            title={t("list.columns.totalRevenue")}
            dataIndex="totalRevenue"
            key="totalRevenue"
            render={(_, price) => (
              <span>{getPriceFormatter(price.currency).format((pricesStats[price.id]?.amount ?? 0) / 100)}</span>
            )}
          />
        ) : null}
        {pricesStats ? (
          <Table.Column<Price>
            title={t("list.columns.projectedRevenue", { defaultValue: "Wartość kontraktów" })}
            dataIndex="projectedRevenue"
            key="projectedRevenue"
            render={(_, price) => (
              <span>{getPriceFormatter(price.currency).format((pricesStats[price.id]?.projected ?? 0) / 100)}</span>
            )}
          />
        ) : null}
        <Table.Column<Price>
          title={t("list.columns.quantityLimit")}
          dataIndex="quantity"
          key="quantity"
          render={(_, price) => {
            const limit = price.config?.quantityLimit;
            const available = Number(limit) - Number(price.clients);
            if (limit) {
              return (
                <Typography.Text type={available === 0 ? "danger" : undefined}>
                  {available ?? "-"}
                  {" / "}
                  {limit ?? "-"}
                </Typography.Text>
              );
            }
            return <span>-</span>;
          }}
        />
        {allowCopyLink ? (
          <Table.Column<Price>
            title={t("list.columns.link")}
            dataIndex="link"
            key="link"
            render={(_, row) => (
              <Tooltip title={t("list.copyToClipboard")}>
                <LinkOutlined
                  style={{ fontSize: 22 }}
                  onClick={() =>
                    navigator.clipboard.writeText(
                      getProductLink({
                        productId,
                        domain: import.meta.env.VITE_ECOMMERCE_DOMAIN,
                        slug: productSlug,
                        ...(row.hidden ? { checkoutId: row.checkoutId } : { priceId: row.id }),
                      }),
                    )
                  }
                />
              </Tooltip>
            )}
          />
        ) : null}
        {!readonly ? (
          <Table.Column<Price>
            title={t("list.columns.active")}
            dataIndex="active"
            key="active"
            render={(active: boolean, row) => {
              const currentlyProcessed = productPricesActions
                ? productPricesActions.find((item) => item.id === row.id)
                : null;
              return (
                <Switch
                  checked={active}
                  disabled={readonly}
                  onChange={() =>
                    dispatch(
                      productActions.updateProductPrices({
                        onFailure: () => {
                          void message.error(t("errorWhenUpdatingPrice"));
                        },
                        payload: {
                          prices: [
                            {
                              active: !active,
                              productId,
                              priceId: row.id,
                              action: ProductPriceAction.ToggleStatus,
                            },
                          ],
                        },
                      }),
                    )
                  }
                  loading={currentlyProcessed ? currentlyProcessed.action === ProductPriceAction.ToggleStatus : false}
                />
              );
            }}
          />
        ) : null}
        {extended && !readonly ? (
          <Table.Column<Price>
            title={
              <div>
                {t("list.columns.hidden")}{" "}
                <Tooltip title={t("list.columns.hiddenInfo")}>
                  <InfoCircleOutlined />
                </Tooltip>
              </div>
            }
            dataIndex="hidden"
            key="hidden"
            render={(hidden: boolean, row) => {
              const currentlyProcessed = productPricesActions
                ? productPricesActions.find((item) => item.id === row.id)
                : null;
              return (
                <Switch
                  checked={hidden}
                  disabled={readonly}
                  checkedChildren={<EyeInvisibleOutlined />}
                  unCheckedChildren={<EyeOutlined />}
                  onChange={() =>
                    dispatch(
                      productActions.updateProductPrices({
                        onFailure: () => {
                          void message.error(t("errorWhenUpdatingPrice"));
                        },
                        payload: {
                          prices: [
                            {
                              hidden: !hidden,
                              productId,
                              priceId: row.id,
                              checkoutId: row.checkoutId || uuid(),
                              action: ProductPriceAction.ToggleVisibility,
                            },
                          ],
                        },
                      }),
                    )
                  }
                  loading={
                    currentlyProcessed ? currentlyProcessed.action === ProductPriceAction.ToggleVisibility : false
                  }
                />
              );
            }}
          />
        ) : (
          <></>
        )}
        {extended && !readonly ? (
          <Table.Column<Price>
            title={t("list.columns.options")}
            key="action"
            align="left"
            render={(_, row) => (
              <PriceConfig
                price={row}
                productId={productId}
                extended={extended}
                canRemove={pricesWithoutArchived.length > 1}
                productPricesActions={productPricesActions}
              />
            )}
          />
        ) : (
          <></>
        )}
      </DraggableTable>
    </div>
  );
};

export default ProductPrices;
